/**
 * 非ラスタ原本 (SVG 等) をラスタ画像しか受け付けない経路に渡す前の取得処理
 * (docs/features/material-raster-rendition-plan.md §3.2)。
 *
 * `resolveMaterialUrls(...).raster` / `buildDragData(...).sourceUrl` は、ラスタ版が未生成の
 * 非ラスタ原本に対して null / 空文字を返す (原本 URL にフォールバックしない)。生成の参考画像
 * (addAsset) や案件画像の選択の入口はここを await し、`POST .../raster` で取得した URL を使ってから進む。
 * 失敗したときは黙って除外せず、`rasterErrorMessage` の文言をトーストで出して中断する。
 */
import { useCallback } from 'react';
import type { GenerationAsset } from '../../components/generation/state/types';
import { isNonRasterOriginal } from '../../components/materials/shared/materialUtils';
import { ensureMaterialRaster, type MaterialFolderType, type MaterialRasterResponse } from './api';
import { useEnsureMaterialRaster } from './hooks';
import { detectIsTemporary, resolveMaterialUrls, type AnyMaterial } from './materialUrl';

export type RasterFetcher = (folderType: MaterialFolderType, materialId: string) => Promise<MaterialRasterResponse>;

/** ラスタ版の取得に失敗したときの既定文言 (BE の `error` が無いとき) */
export const RASTER_UNAVAILABLE_MESSAGE = 'この素材は画像 (PNG) に変換できませんでした';

/** 生成 state の asset にラスタ版の取得が必要か (非ラスタ原本で sourceUrl が未解決) */
export function needsRasterFetch(asset: Pick<GenerationAsset, 'contentType' | 'sourceUrl'>): boolean {
  return isNonRasterOriginal(asset.contentType) && !(asset.sourceUrl ?? '').trim();
}

/**
 * asset の sourceUrl をラスタ版にそろえる。取得が不要ならそのまま返す。
 * 取得に失敗したら axios エラーをそのまま投げる (呼び出し側が rasterErrorMessage でトースト)。
 */
export async function ensureRasterAsset(
  asset: GenerationAsset,
  fetchRaster: RasterFetcher = ensureMaterialRaster,
): Promise<GenerationAsset> {
  if (!needsRasterFetch(asset)) return asset;
  const raster = await fetchRaster(asset.type, asset.id);
  return {
    ...asset,
    sourceUrl: raster.raster_url,
    // 一覧側の thumbnail_url はラスタ版由来の .thumb.jpg。無かった素材は応答の値で補う
    thumbnailUrl: asset.thumbnailUrl || raster.thumbnail_url || raster.raster_url,
    width: raster.width ?? asset.width ?? null,
    height: raster.height ?? asset.height ?? null,
  };
}

/**
 * 素材オブジェクトからラスタ画像の URL を得る (案件画像の選択など、asset を経由しない入口用)。
 * ラスタ原本は fullsize、非ラスタ原本はラスタ版 (無ければ取得)。動画・音声は取得できないので null。
 */
export async function ensureMaterialRasterUrl(
  material: AnyMaterial,
  fetchRaster: RasterFetcher = ensureMaterialRaster,
): Promise<string | null> {
  const urls = resolveMaterialUrls(material);
  if (urls.raster) return urls.raster;
  if (!isNonRasterOriginal(material.content_type)) return null;
  const raster = await fetchRaster(detectIsTemporary(material) ? 'temporary' : 'cloud', material.id);
  return raster.raster_url;
}

/** ラスタ版の取得エラーから利用者向けの文言を取り出す (BE の `error` 優先) */
export function rasterErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: unknown } } } | null)?.response?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  return RASTER_UNAVAILABLE_MESSAGE;
}

/**
 * コンポーネント用: 取得に成功したら該当フォルダの一覧クエリを invalidate する版の ensureRasterAsset。
 * 返す関数は asset を受け取り、ラスタ版をそろえた asset を返す (失敗は throw)。
 */
export function useEnsureRasterAsset(): (asset: GenerationAsset) => Promise<GenerationAsset> {
  const { mutateAsync } = useEnsureMaterialRaster();
  return useCallback(
    (asset: GenerationAsset) =>
      ensureRasterAsset(asset, (folderType, materialId) => mutateAsync({ folderType, materialId })),
    [mutateAsync],
  );
}

/** コンポーネント用: 案件画像の選択などで素材からラスタ URL を得る (成功時に一覧を invalidate) */
export function useEnsureMaterialRasterUrl(): (material: AnyMaterial) => Promise<string | null> {
  const { mutateAsync } = useEnsureMaterialRaster();
  return useCallback(
    (material: AnyMaterial) =>
      ensureMaterialRasterUrl(material, (folderType, materialId) => mutateAsync({ folderType, materialId })),
    [mutateAsync],
  );
}
